import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../../../../../../utils/functions';
import { FaBars, FaLock } from 'react-icons/fa';
import { IoMdArrowDropleft, IoMdArrowDropright } from 'react-icons/io';
import OpenCaja from './components/OpenCaja/OpenCaja';
import CloseCaja from './components/CloseCaja/CloseCaja';
import NewMovement from './components/NewMovement/NewMovement';


interface Caja {
    workstation_id: number;
    workstation_name: string;
    last_modified: string;
    last_opened: string;
    last_closed: string;
    user_id: number;
    user_name: string;
    is_open: boolean,
    initial_amount: number,
    current_amount: number,
    final_amount: number,
}

interface IndividualCajaProps {
    caja: Caja;
    cajas: Caja[];
    setCajas: (cajas: Caja[]) => void;
    setActioncaja?: (closing: boolean) => void;
    isSessionActive?: boolean;
    hasPendientes?: boolean; // if there are pending orders/receipts globally
    openCajasCount?: number; // total number of open cajas
}

const IndividualCaja: React.FC<IndividualCajaProps> = ({
    caja,
    cajas,
    setCajas,
    setActioncaja,
    isSessionActive,
    hasPendientes,
    openCajasCount = 0
}) => {
    const [showOpenCajaModal, setShowOpenCajaModal] = useState(false);
    const [showCloseCajaModal, setShowCloseCajaModal] = useState(false);
    const [showCajaMovementModal, setShowCajaMovementModal] = useState(false);

    // para uso en render: cuando no hay sesión, mostrar comportamiento bloqueado como en Pendings
    const isLocked = !isSessionActive;
    
    // Determine if close button should be disabled based on new business logic
    const shouldDisableClose = isLocked || (openCajasCount === 1 && hasPendientes);

    function formatDateTime(dateTime: string) {
        const date = new Date(dateTime);
        const timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
    
        // if the date is equal to today, return hoy, <time>
        if (date.toDateString() === new Date().toDateString()) {
            return 'Hoy ' + date.toLocaleTimeString(undefined, timeOptions);
        } 
        // if the date is equal to yesterday, return ayer, <time>
        else if (date.toDateString() === new Date(new Date().setDate(new Date().getDate() - 1)).toDateString()) {
            return 'Ayer ' + date.toLocaleTimeString(undefined, timeOptions);
        } 
        // otherwise return the date and time without seconds
        else {
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString(undefined, timeOptions);
        }
    }

return (
    <>
    <div className={`mp-stat-card flex-col items-stretch gap-0 p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg ${isLocked ? 'opacity-60 pointer-events-none relative' : ''} ${!isLocked && !caja.is_open ? 'relative' : ''}`}>
        {isLocked && (
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs opacity-40" style={{ background: 'rgba(107,107,107,0.08)', color: '#6b7280' }}>
            <FaLock />
          </div>
        )}
        
         {/* Header with title and amount */}
         <div className="flex items-center justify-between mb-4">
             <h3 className="text-lg font-semibold flex items-center gap-2 m-0" style={{ color: 'var(--text-color)' }}>
                 {caja.workstation_name}
                 {isLocked && !caja.is_open && <FaLock className="text-sm opacity-40" />}
             </h3>
             <span className="rounded-lg px-3 py-1.5 text-sm font-semibold tabular-nums"
               style={{ background: 'rgba(252,117,86,0.12)', color: '#FC7556', border: '1px solid rgba(252,117,86,0.2)' }}>
                 {formatCurrency(caja.is_open ? caja.current_amount : caja.final_amount)}
             </span>
         </div>

        {/* Info text */}
        {(caja.last_closed !== "None" && caja.last_closed !== null) && (
            <p className="text-sm mb-3 opacity-50 m-0" style={{ color: 'var(--text-color)' }}>
                {caja.is_open ? 'Abierta ' : 'Cerrada '} 
                {caja.is_open ? formatDateTime(caja.last_opened) : formatDateTime(caja.last_closed)}
                {' por ' + caja.user_name}
            </p>
        )}

        {/* Status badge */}
        <div className="mb-4">
            <span className={`mp-badge ${caja.is_open ? 'mp-badge-success' : 'mp-badge-neutral'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${caja.is_open ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                {caja.is_open ? 'Abierta' : 'Cerrada'}
            </span>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
            {caja.is_open ? (
                <>
                    <button 
                        className="mp-btn-secondary flex-1" 
                        onClick={() => setShowCajaMovementModal(true)}
                    >
                        <FaBars /> Movimiento
                    </button>
                    <button 
                        className={`mp-btn-danger flex-1 ${shouldDisableClose ? 'opacity-50 pointer-events-none' : ''}`}
                        onClick={() => {
                            if (!isSessionActive) return;
                            if (shouldDisableClose) return;
                            setShowCloseCajaModal(true);
                        }}
                        disabled={shouldDisableClose}
                        title={isLocked ? 'No hay sesión iniciada' : (openCajasCount === 1 && hasPendientes) ? 'No se puede cerrar: hay pedidos o recibos pendientes y es la última caja abierta' : undefined}
                        aria-disabled={shouldDisableClose}
                    >
                        { isLocked
                          ? <><FaLock /> Cerrar</>
                          : <>Cerrar <IoMdArrowDropleft /></>
                        }
                    </button>
                </>
            ) : (
                <button 
                    className="mp-btn-primary flex-1" 
                    onClick={() => setShowOpenCajaModal(true)}
                >
                    Abrir <IoMdArrowDropright />
                </button>
            )}
        </div>
    </div>
    
    <OpenCaja 
        caja={caja} 
        cajas={cajas} 
        setCajas={setCajas} 
        show={showOpenCajaModal} 
        setShow={setShowOpenCajaModal} 
        setActioncaja={setActioncaja} 
    />
    <CloseCaja 
        caja={caja} 
        cajas={cajas} 
        setCajas={setCajas} 
        show={showCloseCajaModal} 
        setShow={setShowCloseCajaModal} 
        {...(!shouldDisableClose ? { setActioncaja } : {})}
        hasPendientes={hasPendientes}
        openCajasCount={openCajasCount}
    />
    <NewMovement 
        caja={caja} 
        cajas={cajas} 
        setCajas={setCajas} 
        show={showCajaMovementModal} 
        setShow={setShowCajaMovementModal} 
    />
    </>
  );
};

export default IndividualCaja;